import TimeTextInput from '../components/ui/TimeTextInput';
import Dialog from '../components/ui/Dialog';
import { engagementTabNotices } from '../utils/engagementTabModel';
import { bankCellFigures } from '../utils/bankLockedLabel';
import { countActiveDevelopments } from '../utils/organizationCards';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import TaskFormModal from '../components/projects/TaskFormModal';
import TaskRowsTable from '../components/tasks/TaskRowsTable';
import { engagementLabel } from '../utils/engagementLabel';
import { useParams, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { useInternalOrgId } from '../hooks/useInternalOrg';
import { isCompanyOrganization } from '../utils/internalOrgPage';
import { ScreenLink as Link } from '../components/permissions/ScreenLink';
import Kpi from '../components/ui/Kpi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import {
  Building2, Eye, EyeOff, Trash2, Archive,
  LayoutDashboard, Users, FolderKanban, Code2, FileSignature, Coins,
  ListChecks, LifeBuoy, ScrollText, FileStack, Clock, Receipt, TrendingUp, CalendarDays,
  Pencil, Plus, FileSpreadsheet, Loader2, ClipboardList,
} from 'lucide-react';
import CustomerMissingFinancials from '../components/engagement/CustomerMissingFinancials';
import EmptyState from '../components/EmptyState';
import ErrorState from '../components/ErrorState';
import { stateKindForError } from '../components/ui/StateBlock';
import CustomerTabs from '../components/CustomerHub/CustomerTabs';
import type { CustomerTab } from '../components/CustomerHub/CustomerTabs';
import { customerTabLayout } from '../components/CustomerHub/customerTabsModel';
import { oneDecimal } from '../components/CustomerHub/CustomerWorkTypes';
import { HubPanel, HubSurface, HubTable } from '../components/CustomerHub/HubPanel';
import ActionCenter, { HubAttention } from '../components/CustomerHub/ActionCenter';
import HardDeleteCustomerModal from '../components/CustomerHub/HardDeleteCustomerModal';
import WorkHoursTab from '../components/CustomerHub/WorkHoursTab';
import { BillingTab, ProfitabilityTab } from '../components/CustomerHub/FinancialTabs';
import QuotesTab from '../components/CustomerHub/QuotesTab';
import ContractsTab, { ContractFormModal } from '../components/CustomerHub/ContractsTab';
import DocumentsTab from '../components/CustomerHub/DocumentsTab';
import EntityMeetings from '../components/meetings/EntityMeetings';
import CreateContractFromQuoteModal from '../components/sales/CreateContractFromQuoteModal';
import ReasonModal from '../components/sales/ReasonModal';
import { InlineError, Spinner, apiErrorMessage } from '../components/sales/shared';
import Field, { FormErrorSummary, FormServerError, rules, useFormValidation, type FormSchema } from '../components/ui/Field';
import { OrgFormModal } from './OrganizationsPage';
import { CreateEngagementModal } from './EngagementsPage';
import TypedEngagementForm, { isTypedEngagementType } from '../components/engagement/TypedEngagementForm';
import { newBankRedirect } from '../components/engagement/hourBankView';
import ProjectFormModal from '../components/projects/ProjectFormModal';
import { getStatusLabel, getPriorityLabel } from '../types';
import type { Ticket, Contact, Engagement, EngagementType, CustomerHubSummary, Characterization } from '../types';
import { characterizationHref } from '../components/CustomerHub/characterizationModel';
import Badge from '../components/ui/Badge';
import { priorityTone, statusTone } from '../components/ui/tone';
import DetailHeader from '../components/details/DetailHeader';
import DetailSection, { FactGrid } from '../components/details/DetailSection';
import DetailSkeleton from '../components/details/DetailSkeleton';
import ForwardLink from '../components/details/ForwardLink';
import { ContactFormModal } from '../components/contacts/ContactForm';
import { CONTACT_TYPE_LABEL_KEY, isContactType } from '../components/contacts/contactAccessModel';
import type { OverflowMenuItem } from '../components/details/OverflowMenu';
import { formatDate, formatHoursClock, formatNumber } from '../utils/format';
import { toast } from '../store/toastStore';
import { offerUndoStatus } from '../utils/undoableActions';
import NewDiscussionModal from '../components/discussions/NewDiscussionModal';
import { canOpenDiscussion, discussionChatPath } from '../utils/discussionParticipants';
import { canOpenTicket } from '../utils/ticketOrigin';
import { usePermissions } from '../hooks/usePermissions';

/**
 * The Customer Hub — `/organizations/:id`.
 *
 * One customer, one page, many areas (decisions 6-7). The page owns the
 * Header, the context-aware Action Center (driven by the customer-hub
 * summary: state + permissions), the Overview and the tab structure; each tab
 * reads its canonical endpoint (engagements, quotes, contracts, documents,
 * work summary, billing, profitability). Tabs and actions the viewer may not
 * use are not rendered.
 *
 * Two invariants this file must never break:
 *   1. Billing terms belong to the Engagement, not the Customer (תיקון 1).
 *      `billing_model` / `hourly_rate` / `retainer_hours` are never read here.
 *   2. A customer's Engagements are read from the Engagement layer — never
 *      inferred from a legacy Customer field.
 */
const TAB_KEYS = [
  'overview', 'contacts', 'quotes', 'projects', 'developments', 'service-contracts',
  'hour-banks', 'lll', 'tasks', 'service-requests', 'contracts', 'documents',
  'work-hours', 'billing', 'profitability', 'meetings',
] as const;
type TabKey = typeof TAB_KEYS[number];

const isTabKey = (v: string | null): v is TabKey => !!v && (TAB_KEYS as readonly string[]).includes(v);

// P-01/C4 (D-20) — which tabs sit in the row is decided per customer by
// `customerTabLayout` (the engagement types it actually has); the rest are
// under "עוד".

export default function OrganizationPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { internalOnly } = usePermissions();
  // P-36 (D-193, Q-170): a modest "דיון חדש" here, the customer already chosen.
  const [showNewDiscussion, setShowNewDiscussion] = useState(false);

  const [showEditOrg, setShowEditOrg] = useState(false);
  const [showDeleteOrg, setShowDeleteOrg] = useState(false);
  // P-01/C2 — the engagement type chosen in "+ הוספת התקשרות ▾" or a tab "+".
  const [createEngagementType, setCreateEngagementType] = useState<EngagementType | null>(null);
  // D-303: the warranty notice's "התקשרות חדשה" - a choice between an hour bank and a service contract.
  const [pickNewEngagement, setPickNewEngagement] = useState(false);
  // P-07 (D-74 §8) — "פתיחת התקשרות מהאפיון ▾": the card the engagement is opened from.
  const [fromCharacterization, setFromCharacterization] = useState<Characterization | null>(null);
  const closeCreateEngagement = () => { setCreateEngagementType(null); setFromCharacterization(null); };
  const [showContractFromQuote, setShowContractFromQuote] = useState(false);
  const [showCreateContract, setShowCreateContract] = useState(false);
  // "+ איש קשר" in the header opens the contacts tab with its add form open.
  const [contactAddRequest, setContactAddRequest] = useState(0);
  const [archiveMode, setArchiveMode] = useState<'archive' | 'restore' | null>(null);
  const [profitForbidden, setProfitForbidden] = useState(false);
  const onProfitForbidden = useCallback(() => setProfitForbidden(true), []);

  const tabParam = searchParams.get('tab');
  const requestedTab: TabKey = isTabKey(tabParam) ? tabParam : 'overview';
  const openContractId = searchParams.get('contract');

  /** Tab selection lives in the URL so back/forward and sharing behave. */
  const setTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'overview') next.delete('tab');
    else next.set('tab', key);
    next.delete('contract');
    setSearchParams(next, { replace: false });
  };
  const setOpenContract = (contractId: string | null) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'contracts');
    if (contractId) next.set('contract', contractId);
    else next.delete('contract');
    setSearchParams(next, { replace: !contractId });
  };

  /** State + permissions that drive the action center and the tab set. */
  const summaryQuery = useQuery({
    queryKey: ['customer-hub-summary', id],
    queryFn: () => api.getCustomerHubSummary(id!),
    // `/customer-hub/*` is OTE staff only (a customer manager is refused): not asked for a customer login.
    enabled: !!id && (user?.role === 'admin' || user?.role === 'agent'),
  });
  const summary = summaryQuery.data?.data;

  const refreshCustomer = () => {
    queryClient.invalidateQueries({ queryKey: ['organization', id] });
    queryClient.invalidateQueries({ queryKey: ['customer-hub-summary', id] });
    queryClient.invalidateQueries({ queryKey: ['organizations'] });
  };

  const archiveMutation = useMutation({
    mutationFn: ({ mode, reason }: { mode: 'archive' | 'restore'; reason: string }) =>
      mode === 'archive' ? api.archiveOrganization(id!, reason || undefined) : api.restoreOrganization(id!, reason || undefined),
    onSuccess: (_res, vars) => {
      setArchiveMode(null);
      refreshCustomer();
      // P-27 (D-166 s.11): the archive happens at once and "בטל" restores the customer through the server.
      if (vars.mode === 'archive') {
        offerUndoStatus({ message: t('undoToast.customerArchived'), revert: () => api.restoreOrganization(id!), onReverted: refreshCustomer });
      }
    },
  });

  const { data: orgData, isLoading: orgLoading, isError: orgError, error: orgErr, refetch: refetchOrg } = useQuery({
    queryKey: ['organization', id],
    queryFn: () => api.getOrganization(id!),
    enabled: !!id,
  });

  const org = orgData?.data;
  // P-33c (D-188): OTE is the company, not a customer - its page never opens, even from a direct link.
  const internalOrgId = useInternalOrgId();
  const hiddenCompany = isCompanyOrganization(org as { is_internal?: boolean } | undefined, internalOrgId, id);

  // The canonical agents list — the same query the Customers list page uses
  // to populate the account-manager picker in the shared edit form.
  const canManage = user?.role === 'admin' || user?.role === 'agent';
  const isAdmin = user?.role === 'admin';
  // The server is authoritative; the summary tells what this viewer may do here.
  const canWrite = summary ? summary.permissions.can_write : canManage;
  const isGeneralManager = summary ? summary.permissions.is_general_manager : isAdmin;
  const { data: agentsData } = useQuery({
    // QA-R1 #6 — every active OTE employee, whatever their role (the server rule).
    queryKey: ['users', 'ote_staff', 'account-manager-candidates'],
    queryFn: () => api.getUsers({ ote_staff: true, active: true, limit: 200 }),
    enabled: canManage,
  });
  const agents = agentsData?.data ?? [];

  const updateOrgMutation = useMutation({
    mutationFn: ({ id: orgId, data }: { id: string; data: any }) => api.updateOrganization(orgId, data),
    onSuccess: (_res, vars) => {
      // DQ-129: handing the customer to another manager removes this user's access - leave the page with a message instead of a "no permission" screen.
      const newManagerId = vars.data?.account_manager_id as string | undefined;
      if (newManagerId && newManagerId !== user?.id && !isAdmin && !isGeneralManager) {
        setShowEditOrg(false);
        queryClient.invalidateQueries({ queryKey: ['organizations'] });
        toast('success', t('organization.handedOver'));
        navigate('/organizations');
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['organization', id] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setShowEditOrg(false);
    },
  });

  /**
   * Add Engagement — the approved Customer -> Engagement model, opened on the
   * canonical Engagement modal with this Customer already pinned.
   */
  const createEngagementMutation = useMutation({
    mutationFn: (data: { type: EngagementType; name: string; status: any; organization_id?: string }) =>
      api.createEngagement({ ...data, organization_id: id! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagements'] });
      queryClient.invalidateQueries({ queryKey: ['engagements', { organization_id: id }] });
      // D-21: a new engagement may have re-activated the customer.
      refreshCustomer();
      setCreateEngagementType(null);
    },
    onError: (err) => toast('error', apiErrorMessage(err, t('common.saveError'))),
  });

  /** Engagements of THIS customer, read from the canonical Engagement layer. */
  const { data: engagementsData, isLoading: engagementsLoading, isError: engagementsError } = useQuery({
    queryKey: ['engagements', { organization_id: id }],
    queryFn: () => api.getEngagements({ organization_id: id, limit: 200 }),
    enabled: !!id,
  });
  const engagements = engagementsData?.data ?? [];
  const engagementsOf = (type: EngagementType) => engagements.filter((e) => e.type === type);
  // D-272 (D-221): with an active hour bank, "+ new hour bank" opens that bank's "new purchase" instead.
  const requestCreateEngagement = (type: EngagementType, card?: Characterization): void => {
    if (type !== 'hour_bank') { if (card) setFromCharacterization(card); setCreateEngagementType(type); return; }
    void newBankRedirect(() => api.getActiveHourBank(id!)).then((path) => {
      if (path) { navigate(path); return; }
      if (card) setFromCharacterization(card);
      setCreateEngagementType(type);
    });
  };

  // D-303: the "התקשרות חדשה" button of the warranty-ending notice opens a new engagement for this customer.
  // FIX-D300-S2 (D-300 sec.4): the manager's [הוספת בנק שעות] / [הוספת חוזה שירות] open that form directly
  // (?new_engagement=hour_bank / service_contract); '1' keeps opening the type picker as before.
  const newEngagementParam = searchParams.get('new_engagement');
  const directNewType: EngagementType | null = newEngagementParam === 'hour_bank' || newEngagementParam === 'service_contract' ? newEngagementParam : null;
  const wantsNewEngagement = newEngagementParam === '1' || directNewType !== null;
  useEffect(() => {
    if (!wantsNewEngagement) return;
    const next = new URLSearchParams(searchParams);
    next.delete('new_engagement');
    setSearchParams(next, { replace: true });
    if (directNewType) { requestCreateEngagement(directNewType); return; }
    setPickNewEngagement(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsNewEngagement]);

  if (hiddenCompany) return <Navigate to="/organizations" replace />;
  if (orgLoading) {
    return <DetailSkeleton kpis={0} />;
  }
  if (orgError) {
    return (
      <DetailSection>
        {/* F3b-2 — a 403 (an employee with no link to this customer) is "אין
            הרשאה", not "תקלה זמנית"; a 404 is "not available". Only a real
            failure keeps the customer's own text and the retry. */}
        <ErrorState
          error={orgErr}
          message={stateKindForError(orgErr) === 'error' ? t('organization.loadError') : undefined}
          onRetry={() => refetchOrg()}
        />
      </DetailSection>
    );
  }
  if (!org) {
    return (
      <DetailSection>
        <EmptyState
          icon={Building2}
          title={t('organization.notFound')}
          action={<Link to="/organizations" className="btn btn-secondary">{t('common.backToOrganizations')}</Link>}
        />
      </DetailSection>
    );
  }


  // DQ-133 (D-313): hidden until the summary says the viewer may see it - an employee opening ?tab=billing no longer fires the 403 request.
  const showBilling = !!summary && summary.permissions.can_view_billing;
  const showProfitability = !!summary?.permissions.can_view_profitability && !profitForbidden;
  // P-01/C4 — the engagement types this customer actually has (any status,
  // archived engagements are not listed), for the dynamic tab row.
  const presentTypes = new Set(engagements.map((e) => e.type));
  // P-07 (D-74 §5, §6) — "באפיון": an active card and no open engagement yet.
  const inCharacterization = !!summary?.characterization?.in_characterization;
  const layout = customerTabLayout(presentTypes, { inCharacterization });
  const TAB_DEFS: Record<TabKey, CustomerTab> = {
    overview: { key: 'overview', label: t('organization.tabOverview'), icon: LayoutDashboard },
    contacts: { key: 'contacts', label: t('organization.tabContacts'), icon: Users },
    quotes: { key: 'quotes', label: t('organization.tabQuotes'), icon: FileSpreadsheet },
    projects: { key: 'projects', label: t('organization.tabProjects'), icon: FolderKanban },
    developments: { key: 'developments', label: t('organization.tabDevelopments'), icon: Code2 },
    'service-contracts': { key: 'service-contracts', label: t('organization.tabServiceContracts'), icon: FileSignature },
    'hour-banks': { key: 'hour-banks', label: t('organization.tabHourBanks'), icon: Coins },
    lll: { key: 'lll', label: t('organization.tabLll'), icon: LifeBuoy },
    tasks: { key: 'tasks', label: t('organization.tabTasks'), icon: ListChecks },
    'service-requests': { key: 'service-requests', label: t('organization.tabServiceRequests'), icon: LifeBuoy },
    contracts: { key: 'contracts', label: t('organization.tabContracts'), icon: ScrollText },
    documents: { key: 'documents', label: t('organization.tabDocuments'), icon: FileStack },
    'work-hours': { key: 'work-hours', label: t('organization.tabWorkHours'), icon: Clock },
    billing: { key: 'billing', label: t('organization.tabBilling'), icon: Receipt },
    profitability: { key: 'profitability', label: t('organization.tabProfitability'), icon: TrendingUp },
    // P-38b (D-192 §12): behind "עוד" (not in the primary row).
    meetings: { key: 'meetings', label: t('organization.tabMeetings'), icon: CalendarDays },
  };
  // P-18 (D-134): the Contacts tab follows the customer's own read rule —
  // whoever may open this customer sees its contacts (an employee who works
  // with it: read only; the Account Manager and the GM: read and edit).
  // Billing and profitability keep their existing gates.
  const hidden = new Set<string>([
    ...(showBilling ? [] : ['billing']),
    ...(showProfitability ? [] : ['profitability']),
  ]);
  const tabs: CustomerTab[] = [...layout.order, 'meetings']
    .filter((key) => !hidden.has(key) && isTabKey(key))
    .map((key) => TAB_DEFS[key as TabKey]);
  // A tab the viewer cannot see (e.g. a shared link) falls back to the overview.
  const activeTab: TabKey = tabs.some((tb) => tb.key === requestedTab) ? requestedTab : 'overview';
  const acceptedQuotes = summary?.accepted_quotes_without_contract ?? [];
  const canAddEngagement = canWrite; // DQ-134 (D-30 exception, D-313): also on an archived customer
  const addEngagementButton = (type: EngagementType, labelKey: string) => (canAddEngagement ? (
    <button type="button" className="btn btn-secondary" onClick={() => requestCreateEngagement(type)}>
      <Plus size={16} aria-hidden="true" /> {t(labelKey)}
    </button>
  ) : (engagementTabNotices({ count: 0, canWrite }).showNoPermissionNotice ? (
    <span className="text-sm text-surface-500" data-testid="no-add-engagement-permission">{t('organization.noAddEngagementPermission')}</span>
  ) : undefined));

  // Page-level actions that join the hub's "⋯" menu: edit (reuses the
  // canonical Customer form) and the exceptional, irreversible hard delete
  // (GM only) — deliberately last and styled as destructive.
  const extraMenuItems: OverflowMenuItem[] = [
    ...(canWrite ? [{ key: 'edit', label: t('organizations.edit'), icon: <Pencil size={16} />, onSelect: () => setShowEditOrg(true) }] : []),
    ...(isGeneralManager ? [{ key: 'hard-delete', label: t('organization.hardDelete.button'), icon: <Trash2 size={16} />, danger: true, onSelect: () => setShowDeleteOrg(true) }] : []),
  ];

  // P-01/C1 — one compact line: account manager · tax id (when set) · phone ·
  // email · "לקוח מאז {joined}".
  const headerLine = [
    org.account_manager_name || t('common.notSpecified'),
    org.tax_id ? <span key="tax">{t('organization.taxId')} <bdi dir="ltr">{org.tax_id}</bdi></span> : null,
    org.phone ? <bdi key="phone" dir="ltr" className="tabular-nums">{org.phone}</bdi> : null,
    org.email ? <bdi key="email" dir="ltr">{org.email}</bdi> : null,
    org.joined_at ? <span key="since" className="tabular-nums">{t('organization.customerSince', { date: formatDate(org.joined_at) })}</span> : null,
  ].filter(Boolean);

  return (
    <div className="max-w-7xl space-y-6">
      <DetailHeader
        breadcrumb={[{ label: t('organizations.title'), href: '/organizations' }, { label: org.name }]}
        title={org.name}
        badges={
          /* Status is the Customer's own `active` flag — never a billing state. */
          <>
            <Badge tone={org.active ? 'success' : 'neutral'} dot>
              {org.active ? t('organizations.statusActive') : t('organizations.statusInactive')}
            </Badge>
            {inCharacterization && <Badge tone="info" dot>{t('characterization.inCharacterization')}</Badge>}
          </>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {headerLine.map((part, i) => (
              <span key={i} className="inline-flex items-center gap-x-2">
                {part}
                {i < headerLine.length - 1 && <span className="text-surface-400" aria-hidden="true">·</span>}
              </span>
            ))}
          </span>
        }
        actions={
          /* Action center (state- and permission-aware) */
          <ActionCenter
            orgId={id!}
            summary={summary}
            summaryLoading={summaryQuery.isLoading}
            summaryError={summaryQuery.isError}
            canAddContact={canWrite && org.active}
            onAddEngagement={(type) => requestCreateEngagement(type)}
            onAddContact={() => { setContactAddRequest((n) => n + 1); setTab('contacts'); }}
            onCreateContract={() => (acceptedQuotes.length > 0 ? setShowContractFromQuote(true) : setShowCreateContract(true))}
            onArchive={() => { archiveMutation.reset(); setArchiveMode('archive'); }}
            onRestore={() => { archiveMutation.reset(); setArchiveMode('restore'); }}
            extraMenuItems={extraMenuItems}
          />
        }
      >
        <HubAttention summary={summary} />
      </DetailHeader>

      {!org.active && (
        <p className="flex items-center gap-2 rounded-lg bg-surface-100 px-4 py-2.5 text-sm text-surface-700" role="status">
          <Archive size={16} className="text-surface-500 flex-shrink-0" aria-hidden="true" />
          {t('organization.actions.archivedNotice')}
        </p>
      )}
      <CustomerMissingFinancials orgId={id!} />

      {/* ── Summary cards (P-01/C3) ─────────────────────────────────────── */}
      {!engagementsLoading && !engagementsError && (
        <SummaryCards orgId={id!} engagements={engagements} presentTypes={presentTypes} summary={summary} />
      )}

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <CustomerTabs active={activeTab} onChange={setTab} tabs={tabs} primary={layout.primary} />

      {/* ── Tab content ────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <OverviewTab
          orgId={id!}
          org={org}
          activeCharacterizations={inCharacterization ? summary?.characterization?.active ?? [] : []}
        />
      )}

      {activeTab === 'contacts' && (
        <ContactsTab
          key={contactAddRequest}
          orgId={id!}
          orgName={org.name}
          canManage={canWrite && org.active}
          canGrantAccess={canWrite && org.active}
          canOpenAccessPage={isGeneralManager}
          startAdding={contactAddRequest > 0}
        />
      )}

      {activeTab === 'quotes' && (
        <QuotesTab
          orgId={id!}
          orgName={org.name}
          canWrite={canWrite && org.active}
          canAddCharacterization={canWrite}
          leadsConverted={summary?.counts.leads_converted}
          onOpenEngagement={(type, card) => requestCreateEngagement(type, card)}
          onOpenDocuments={() => setTab('documents')}
        />
      )}

      {/* P-01/C4 — one tab per engagement type, each with its own "+". */}
      {activeTab === 'projects' && (
        <EngagementListTab
          engagements={engagementsOf('project')}
          isLoading={engagementsLoading}
          isError={engagementsError}
          typeLabel={t('engagementDetail.types.project')}
          emptyText={t('organization.noProjectsForCustomer')}
          addButton={addEngagementButton('project', 'organization.addProject')}
        />
      )}

      {activeTab === 'developments' && (
        <EngagementListTab
          engagements={engagementsOf('development')}
          isLoading={engagementsLoading}
          isError={engagementsError}
          typeLabel={t('engagementDetail.types.development')}
          emptyText={t('organization.noDevelopmentsForCustomer')}
          addButton={addEngagementButton('development', 'organization.addDevelopment')}
        />
      )}

      {/* חוזי שירות — Service Contract engagements only; לל"ח has its own tab. */}
      {activeTab === 'service-contracts' && (
        <EngagementListTab
          engagements={engagementsOf('service_contract')}
          isLoading={engagementsLoading}
          isError={engagementsError}
          typeLabel={t('engagementDetail.types.service_contract')}
          emptyText={t('organization.noServiceContracts')}
          addButton={addEngagementButton('service_contract', 'organization.addServiceContract')}
        />
      )}

      {activeTab === 'hour-banks' && (
        <EngagementListTab
          engagements={engagementsOf('hour_bank')}
          isLoading={engagementsLoading}
          isError={engagementsError}
          typeLabel={t('engagementDetail.types.hour_bank')}
          emptyText={t('organization.noHourBanks')}
          showHourBankBalance
          addButton={addEngagementButton('hour_bank', 'organization.addHourBank')}
        />
      )}

      {activeTab === 'lll' && (
        <EngagementListTab
          engagements={engagementsOf('service_without_contract')}
          isLoading={engagementsLoading}
          isError={engagementsError}
          typeLabel={t('organization.tabLll')}
          emptyText={t('organization.noLll')}
          addButton={addEngagementButton('service_without_contract', 'organization.addLll')}
        />
      )}

      {/* P-02b (D-36 Q-P9): every task of the customer, any engagement; "+ משימה חדשה" with the customer locked. */}
      {activeTab === 'tasks' && <TicketsTab orgId={id!} mode="task" canCreateTask={canWrite && org.active} />}
      {activeTab === 'service-requests' && (
        <TicketsTab
          orgId={id!}
          mode="ticket"
          addButton={canWrite && org.active ? (
            <>
              {/* P-45L (D-210): an employee opens no ticket - the customer does, in the portal. */}
              {canOpenTicket(user) && (
                <button type="button" className="btn btn-secondary" onClick={() => navigate(`/tickets?create=true&organization_id=${id}`)}>
                  <Plus size={16} aria-hidden="true" /> {t('organization.openRequest')}
                </button>
              )}
              {canOpenDiscussion(user, internalOnly) && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowNewDiscussion(true)}>
                  {t('discussions.newConversation')}
                </button>
              )}
            </>
          ) : undefined}
        />
      )}
      {showNewDiscussion && (
        <NewDiscussionModal
          initialOrganizationId={id}
          initialKind="conversation"
          onClose={() => setShowNewDiscussion(false)}
          onCreated={(created) => { setShowNewDiscussion(false); navigate(discussionChatPath(created.id)); }}
        />
      )}
      {activeTab === 'contracts' && (
        <ContractsTab
          orgId={id!}
          canWrite={canWrite && org.active}
          acceptedQuotes={acceptedQuotes}
          openContractId={openContractId}
          onOpenContract={setOpenContract}
        />
      )}
      {/* D-308 (QX-1319): the explorer asks the server who may write (GM / AM / PM-TL of an active project). */}
      {activeTab === 'documents' && (
        <DocumentsTab orgId={id!} canWrite={org.active} />
      )}
      {activeTab === 'meetings' && (
        <DetailSection title={t('organization.tabMeetings')} flush>
          <EntityMeetings organizationId={id!} canCreate={canWrite && org.active} bare />
        </DetailSection>
      )}
      {activeTab === 'work-hours' && <WorkHoursTab orgId={id!} />}
      {activeTab === 'billing' && <BillingTab orgId={id!} />}
      {activeTab === 'profitability' && (
        <ProfitabilityTab orgId={id!} onForbidden={onProfitForbidden} />
      )}

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      {showEditOrg && (
        <OrgFormModal
          org={org}
          agents={agents}
          onClose={() => { setShowEditOrg(false); updateOrgMutation.reset(); }}
          onSubmit={(rawData) => {
            const { _contacts, ...fields } = rawData;
            const data = Object.fromEntries(
              Object.entries(fields).filter(([, v]) => v !== '' && v !== null)
            );
            // Clearing these two in the form must CLEAR the column rather than
            // silently keep the old value (both are nullable by design).
            for (const k of ['phone_secondary', 'joined_at'] as const) {
              if (fields[k] === '' || fields[k] === null) data[k] = null;
            }
            updateOrgMutation.mutate({ id: id!, data });
          }}
          isLoading={updateOrgMutation.isPending}
          error={updateOrgMutation.error}
          isAdmin={isAdmin}
          canManage={canManage}
          t={t}
        />
      )}

      {/* P-03/B (D-31) — a service contract / hour bank / לל"ח opens its own form (customer locked). */}
      {createEngagementType && isTypedEngagementType(createEngagementType) && (
        <TypedEngagementForm
          mode="create"
          type={createEngagementType}
          organizationId={id!}
          organizationName={org.name}
          initialName={fromCharacterization?.title}
          characterizationId={fromCharacterization?.id}
          onClose={closeCreateEngagement}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['engagements'] });
            queryClient.invalidateQueries({ queryKey: ['engagements', { organization_id: id }] });
            queryClient.invalidateQueries({ queryKey: ['characterizations', id] });
            refreshCustomer();
            closeCreateEngagement();
            toast('success', t('engagementPage.created'));
          }}
        />
      )}
      {/* P-04/B (D-31) — a project / development opens the project form (customer locked), then its page. */}
      {(createEngagementType === 'project' || createEngagementType === 'development') && (
        <ProjectFormModal
          mode="create"
          kind={createEngagementType}
          organizationId={id!}
          organizationName={org.name}
          initialName={fromCharacterization?.title}
          characterizationId={fromCharacterization?.id}
          onClose={closeCreateEngagement}
          onSaved={(projectId) => {
            queryClient.invalidateQueries({ queryKey: ['engagements'] });
            queryClient.invalidateQueries({ queryKey: ['engagements', { organization_id: id }] });
            queryClient.invalidateQueries({ queryKey: ['characterizations', id] });
            refreshCustomer();
            closeCreateEngagement();
            navigate(`/projects/${projectId}`);
          }}
        />
      )}
      {createEngagementType && !isTypedEngagementType(createEngagementType)
        && createEngagementType !== 'project' && createEngagementType !== 'development' && (
        <CreateEngagementModal
          lockedOrganizationId={id!}
          initialType={createEngagementType}
          onClose={() => setCreateEngagementType(null)}
          isLoading={createEngagementMutation.isPending}
          onSubmit={(data) => createEngagementMutation.mutate(data)}
        />
      )}

      {pickNewEngagement && (
        <Dialog open onClose={() => setPickNewEngagement(false)} title={t('notifications.newEngagementPick.title')} size="sm">
          <div className="space-y-3" data-testid="new-engagement-pick">
            <p className="text-sm">{t('notifications.newEngagementPick.body')}</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn btn-primary" onClick={() => { setPickNewEngagement(false); requestCreateEngagement('hour_bank'); }}>
                {t('notifications.newEngagementPick.hourBank')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => { setPickNewEngagement(false); requestCreateEngagement('service_contract'); }}>
                {t('notifications.newEngagementPick.serviceContract')}
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {showDeleteOrg && (
        <HardDeleteCustomerModal
          orgId={id!}
          orgName={org.name}
          onClose={() => setShowDeleteOrg(false)}
          onDeleted={() => {
            setShowDeleteOrg(false);
            queryClient.invalidateQueries({ queryKey: ['organizations'] });
            navigate('/organizations');
          }}
        />
      )}

      {archiveMode && (
        <ReasonModal
          title={archiveMode === 'archive' ? t('organization.archive.title') : t('organization.archive.restoreTitle')}
          description={archiveMode === 'archive' ? t('organization.archive.description') : t('organization.archive.restoreDescription')}
          confirmLabel={archiveMode === 'archive' ? t('organization.actions.archive') : t('organization.actions.restore')}
          reasonLabel={t('organization.archive.reason')}
          loading={archiveMutation.isPending}
          error={archiveMutation.isError ? apiErrorMessage(archiveMutation.error, t('organization.archive.failed')) : null}
          onConfirm={(reason) => archiveMutation.mutate({ mode: archiveMode, reason })}
          onCancel={() => setArchiveMode(null)}
        />
      )}

      {showContractFromQuote && acceptedQuotes.length > 0 && (
        <CreateContractFromQuoteModal
          quotes={acceptedQuotes}
          onClose={() => setShowContractFromQuote(false)}
          onCreated={(contract) => {
            setShowContractFromQuote(false);
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
            queryClient.invalidateQueries({ queryKey: ['customer-hub-summary', id] });
            setOpenContract(contract.id);
          }}
        />
      )}

      {/* "יצירת חוזה" with no accepted quote: the existing contract form. */}
      {showCreateContract && (
        <ContractFormModal
          orgId={id!}
          onClose={() => setShowCreateContract(false)}
          onSaved={(contract) => {
            setShowCreateContract(false);
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
            queryClient.invalidateQueries({ queryKey: ['customer-hub-summary', id] });
            setOpenContract(contract.id);
          }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Summary cards (P-01/C3)
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The cards above the tabs — only the ones that are relevant to this customer:
 * one per engagement type it has (any non-archived status), plus open tasks
 * and open requests (always). Each card opens its tab. Engagement counts come
 * from the customer's engagement list; the service-contract usage (this
 * month, consumption.service) and the hour-bank balance (hourBank.service)
 * come from the hub summary.
 */
function SummaryCards({ orgId, engagements, presentTypes, summary }: {
  orgId: string;
  engagements: Engagement[];
  presentTypes: Set<EngagementType>;
  summary: CustomerHubSummary | undefined;
}) {
  const { t } = useTranslation();
  const activeOf = (type: EngagementType) =>
    engagements.filter((e) => e.type === type && e.child_status === 'active').length;
  const cards: { key: string; tab: string; label: string; value: ReactNode }[] = [];

  if (presentTypes.has('project')) {
    cards.push({ key: 'projects', tab: 'projects', label: t('organization.cardActiveProjects'), value: activeOf('project') });
  }
  if (presentTypes.has('development')) {
    cards.push({ key: 'developments', tab: 'developments', label: t('organization.cardActiveDevelopments'), value: countActiveDevelopments(engagements) });
  }
  if (presentTypes.has('service_contract') && summary) {
    const usage = summary.service_contract_usage ?? [];
    const first = usage[0];
    cards.push({
      key: 'service', tab: 'service-contracts', label: t('organization.cardServiceContract'),
      value: !first ? t('organization.noActiveContract') : (
        <>
          {/* FU-14 / F1-1 — the same format as the bar on the contract page:
              "H:MM מתוך X ש' (+N ש' מאושרות)". */}
          {first.quota != null
            ? ((first.extra_hours ?? 0) > 0 && first.base_quota != null
              ? t('organization.serviceUsageExtra', { used: formatHoursClock(Math.round(first.consumed_hours * 60)), quota: formatNumber(first.base_quota, 2), extra: formatNumber(first.extra_hours, 2) })
              : t('organization.serviceUsage', { used: formatHoursClock(Math.round(first.consumed_hours * 60)), quota: formatNumber(first.base_quota ?? first.quota, 2) }))
            : t('organization.serviceUsageNoQuota', { used: formatHoursClock(Math.round(first.consumed_hours * 60)) })}
          {usage.length > 1 && <span className="ms-1.5 text-surface-500">{t('organization.moreContracts', { count: usage.length - 1 })}</span>}
        </>
      ),
    });
  }
  if (presentTypes.has('hour_bank') && summary) {
    const bank = summary.work_types?.find((w) => w.type === 'hour_bank');
    cards.push({
      key: 'hourbank', tab: 'hour-banks', label: t('organization.cardHourBank'),
      value: t('organization.hoursLeft', { hours: oneDecimal(bank?.hour_bank_balance ?? 0) }),
    });
  }
  if (presentTypes.has('service_without_contract')) {
    cards.push({
      key: 'lll', tab: 'lll', label: t('organization.cardLll'),
      value: activeOf('service_without_contract') > 0 ? t('organization.lllActive') : t('organization.lllInactive'),
    });
  }
  if (summary) {
    cards.push({ key: 'tasks', tab: 'tasks', label: t('organization.cardOpenTasks'), value: summary.counts.open_tasks ?? 0 });
    cards.push({ key: 'requests', tab: 'service-requests', label: t('organization.cardOpenRequests'), value: summary.counts.open_service_requests });
  }
  if (cards.length === 0) return null;

  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
      {/* P-22 (D-156 #8) - the shared card component (ui/Kpi); a card that is a sentence, not a number, uses a smaller value. */}
      {cards.map((c) => (
        <li key={c.key} className={c.key === 'service' ? 'col-span-2' : undefined}>
          <Kpi
            to={`/organizations/${orgId}?tab=${c.tab}`}
            label={c.label}
            value={typeof c.value === 'number' ? c.value : <span className="text-base sm:text-lg">{c.value}</span>}
            className="h-full"
          />
        </li>
      ))}
    </ul>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Overview
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The Overview shows only what is canonically derivable from the Customer and
 * its Engagements. Nothing is computed on the client and no counter is
 * invented: where a value cannot be obtained truthfully it is not shown.
 */
function OverviewTab({ orgId, org, activeCharacterizations }: {
  orgId: string;
  org: any;
  /** P-07 (D-74 §6) — the active cards while the customer is "באפיון" (else empty). */
  activeCharacterizations: { id: string; title: string }[];
}) {
  const { t } = useTranslation();
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');

  const { data: directTicketsData, isLoading: ticketsLoading, isError: ticketsError } = useQuery({
    queryKey: ['org-direct-tickets', orgId],
    // QA-R1 #5 — "direct tickets" are service tickets only (task_type ≠ 'task'):
    // a contract / bank task has no project either, yet it is not a ticket.
    queryFn: () => api.getOrganizationTickets(orgId, { direct_only: 'true', task_type: 'ticket,discussion', limit: 20 }),
    enabled: !!orgId,
  });

  const directTickets = directTicketsData?.data ?? [];

  // P-01/C3 — the engagement cards moved above the tabs (SummaryCards), so
  // the overview keeps only direct tickets, the customer details and SLA.
  // DQ-128: same definition as the header counter (service tickets only, discussions are listed but not counted).
  const openTickets = directTickets.filter((tk: Ticket) => tk.task_type === 'ticket' && tk.status !== 'completed' && tk.status !== 'closed').length;

  // Secondary business details, shown only when they exist.
  const details: { label: string; value: ReactNode; ltr?: boolean }[] = [
    ...(org.tax_id ? [{ label: t('organization.taxId'), value: org.tax_id, ltr: true }] : []),
    ...(org.phone_secondary ? [{ label: t('organization.phoneSecondary'), value: org.phone_secondary, ltr: true }] : []),
    ...(org.city ? [{ label: t('uxDetails.city'), value: org.city }] : []),
    ...(org.address ? [{ label: t('uxDetails.address'), value: org.address }] : []),
    ...(org.website ? [{ label: t('uxDetails.website'), value: org.website, ltr: true }] : []),
    /* תיקון 1 — billing terms belong to the Engagement, not the Customer, so
       no `billing_model` / `hourly_rate` appears here. */
    {
      label: t('uxDetails.ticketVisibility'),
      value: (
        <span className="inline-flex items-center gap-1.5">
          {org.ticket_visibility === 'all'
            ? <><Eye size={14} className="text-surface-500" aria-hidden="true" /> {t('organization.allTickets')}</>
            : <><EyeOff size={14} className="text-surface-500" aria-hidden="true" /> {t('organization.ownTickets')}</>}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-8">
      {activeCharacterizations.length > 0 && (
        <ul className="space-y-1.5" aria-label={t('characterization.sectionTitle')}>
          {activeCharacterizations.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-primary-100 bg-primary-50 px-4 py-2.5 text-sm text-surface-800">
              <ClipboardList size={16} className="text-primary-700 flex-shrink-0" aria-hidden="true" />
              <span className="font-medium">{t('characterization.overviewLine', { title: c.title })}</span>
              <Link to={characterizationHref(orgId, c.id)} className="font-medium text-primary-700 hover:underline underline-offset-2">
                {t('characterization.overviewLink')}
              </Link>
            </li>
          ))}
        </ul>
      )}
      <HubPanel
        title={t('organization.directTickets')}
        subtitle={t('organization.noProject')}
        action={
          !ticketsLoading && !ticketsError && directTickets.length > 0 ? (
            <span className="text-xs text-surface-600 tabular-nums">
              {t('organization.openCount', { count: openTickets })}
            </span>
          ) : undefined
        }
      >
        {ticketsLoading ? (
          <Spinner />
        ) : ticketsError ? (
          <InlineError>{t('tickets.loadError')}</InlineError>
        ) : directTickets.length === 0 ? (
          <HubSurface>
            <p className="px-4 py-6 text-center text-sm text-surface-600">{t('organization.noDirectTickets')}</p>
          </HubSurface>
        ) : (
          <HubSurface>
            <TicketRows tickets={directTickets} showPriority />
          </HubSurface>
        )}
      </HubPanel>

      <HubPanel title={t('uxDetails.customerDetails')}>
        <HubSurface padded>
          <FactGrid items={details} columns={3} />
        </HubSurface>
      </HubPanel>

      {/* SLA settings stay an admin-only area of the Customer record. */}
      {isAdmin && <SlaSettingsSection orgId={orgId} org={org} />}
    </div>
  );
}

/** Quiet ticket rows (title → assignee · date, priority + status badges), each a link. */
function TicketRows({ tickets, showPriority = false, showEngagement = false }: {
  tickets: Ticket[];
  showPriority?: boolean;
  /** P-02b (§6.4) — the task's engagement ("name · type") on its row. */
  showEngagement?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <ul className="divide-y divide-surface-100">
      {tickets.map((ticket) => (
        <li key={ticket.id}>
          <Link
            to={`/tickets/${ticket.id}`}
            className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-surface-50 transition-colors duration-150"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-surface-900 truncate">{ticket.title}</p>
              <p className="text-xs text-surface-600 mt-0.5">
                {showEngagement && (
                  <>
                    <span className="sr-only">{t('tickets.engagement')}: </span>
                    <span className="whitespace-nowrap"><bdi>{engagementLabel(ticket.engagement_name, ticket.engagement_type, t) || '—'}</bdi><span className="mx-1.5 text-surface-400" aria-hidden="true">·</span></span>
                  </>
                )}
                <span className="whitespace-nowrap">{ticket.agent_name || t('common.unassigned')}<span className="mx-1.5 text-surface-400" aria-hidden="true">·</span></span>
                <span className="tabular-nums">{formatDate(ticket.created_at)}</span>
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {showPriority && ticket.priority && ticket.priority !== 'medium' && ticket.priority !== 'low' && (
                <Badge variant="outline" tone={priorityTone(ticket.priority)}>{getPriorityLabel(ticket.priority, t)}</Badge>
              )}
              <Badge tone={statusTone(ticket.status)} dot>{getStatusLabel(ticket.status, t)}</Badge>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Contacts
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The customer's contacts (P-18): the same table facts as the Contacts page
 * (type, primary, approver, access) and the SAME contact form (D-135) —
 * add with the customer fixed, edit per row. An employee who works with the
 * customer sees the list read-only (D-134).
 *
 * A contact is NEVER an OTE employee — different identities. `portal_access`
 * remains the canonical Portal access source; the form only SHOWS it (D-136).
 */
function ContactsTab({ orgId, orgName, canManage, canGrantAccess, canOpenAccessPage, startAdding = false }: {
  orgId: string;
  orgName: string;
  canManage: boolean;
  /** D-147 (QX-79): the GM, and this customer's Account Manager. */
  canGrantAccess: boolean;
  canOpenAccessPage: boolean;
  /** "+ איש קשר" in the header: open with the add form shown. */
  startAdding?: boolean;
}) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(startAdding && canManage);
  const [editing, setEditing] = useState<Contact | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['contacts', { organization_id: orgId }],
    queryFn: () => api.getContacts({ organization_id: orgId, limit: 200 }),
    enabled: !!orgId,
  });
  const contacts = data?.data ?? [];

  return (
    <HubPanel
      title={t('admin.contacts')}
      subtitle={t('contacts.roleHint')}
      action={
        <>
          <ForwardLink to={`/contacts?org=${orgId}`}>{t('organization.openContactsScreen')}</ForwardLink>
          {canManage && (
            <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
              <Plus size={16} aria-hidden="true" /> {t('organization.actions.addContact')}
            </button>
          )}
        </>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <InlineError>{t('common.error')}</InlineError>
      ) : contacts.length === 0 ? (
        <HubSurface>
          <EmptyState icon={Users} title={t('contacts.empty')} />
        </HubSurface>
      ) : (
        <HubSurface>
          <HubTable>
            <table className="table-modern">
              <thead>
                <tr>
                  <th>{t('contacts.name')}</th>
                  <th>{t('contacts.contactType')}</th>
                  <th className="hidden md:table-cell">{t('contacts.email')}</th>
                  <th className="hidden sm:table-cell">{t('contacts.phone')}</th>
                  <th className="hidden lg:table-cell">{t('contacts.portalAccessSection')}</th>
                  {canManage && <th className="w-12"><span className="sr-only">{t('common.actions')}</span></th>}
                </tr>
              </thead>
              <tbody>
                {contacts.map((c: Contact) => (
                  <tr key={c.id}>
                    <td>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium text-surface-900">{c.name || '—'}</span>
                        {c.is_primary && <Badge tone="info">{t('contacts.primary')}</Badge>}
                        {c.is_ticket_approver && <Badge tone="warning">{t('portalAccess.approveTickets')}</Badge>}
                      </span>
                      {c.role_title && <span className="block text-xs text-surface-600">{c.role_title}</span>}
                      {c.email && <span className="md:hidden block text-xs text-surface-600" dir="ltr">{c.email}</span>}
                    </td>
                    <td><span className="text-surface-600">{isContactType(c.contact_role) ? t(CONTACT_TYPE_LABEL_KEY[c.contact_role]) : t('contacts.typeUnclassifiedShort')}</span></td>
                    <td className="hidden md:table-cell"><span dir="ltr">{c.email || '—'}</span></td>
                    <td className="hidden sm:table-cell tabular-nums"><span dir="ltr">{c.phone || c.mobile || '—'}</span></td>
                    <td className="hidden lg:table-cell">
                      {c.portal_access_id
                        ? <Badge tone={c.portal_access_active ? 'success' : 'neutral'} dot>{c.portal_access_active ? t('contacts.accessActive') : t('contacts.accessInactive')}</Badge>
                        : <span className="text-surface-500">—</span>}
                    </td>
                    {canManage && (
                      <td>
                        {c.can_edit && (
                          <button type="button" className="btn-icon p-1.5 text-surface-500 hover:text-surface-900 hover:bg-surface-100"
                            onClick={() => setEditing(c)} aria-label={t('contacts.editNamed', { name: c.name })} title={t('common.edit')}>
                            <Pencil size={16} aria-hidden="true" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </HubTable>
        </HubSurface>
      )}

      {(adding || editing) && (
        // P-18 (D-135): the ONE contact form, with this customer fixed.
        <ContactFormModal
          contact={editing}
          organizations={[{ id: orgId, name: orgName }]}
          fixedOrgId={orgId}
          canGrantAccess={canGrantAccess}
          canOpenAccessPage={canOpenAccessPage}
          onClose={() => { setAdding(false); setEditing(null); }}
        />
      )}
    </HubPanel>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Engagement lists (projects / developments / service contracts / hour banks)
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * A customer-filtered list of one Engagement type, linking to the canonical
 * Engagement detail screen. No new business logic: the rows come straight from
 * the Engagement layer.
 *
 * Note on scope: `GET /api/engagements` scopes non-admins to their own
 * `organization_id` rather than through the Repair 2 customer scope. It cannot
 * leak (it only ever narrows), but for a viewer whose own organization differs
 * the list may come back empty even though the customer has engagements. We
 * therefore render a truthful empty state and never a fabricated count.
 */
function EngagementListTab({
  engagements,
  isLoading,
  isError,
  typeLabel,
  showHourBankBalance = false,
  addButton,
}: {
  engagements: Engagement[];
  isLoading: boolean;
  isError: boolean;
  typeLabel: string;
  emptyText?: string; // superseded by D-267 generic empty text
  /** Hour banks: add the live balance column (one snapshot call per bank). */
  showHourBankBalance?: boolean;
  /** P-01/C4 — "+ … חדש": the engagement form with this type chosen. */
  addButton?: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <HubPanel
      title={typeLabel}
      action={
        <>
          <ForwardLink to="/engagements">{t('organization.openEngagementsScreen')}</ForwardLink>
          {addButton}
        </>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <InlineError>{t('organization.engagementsLoadError')}</InlineError>
      ) : engagementTabNotices({ count: engagements.length, canWrite: true }).showEmpty ? (
        <HubSurface>
          <EmptyState icon={FolderKanban} title={t('organization.noEngagementsYet', { type: typeLabel })} />
        </HubSurface>
      ) : (
        <HubSurface>
          <HubTable>
            <table className="table-modern">
              <thead>
                <tr>
                  <th>{t('engagements.name')}</th>
                  <th className="hidden sm:table-cell">{t('engagements.type')}</th>
                  {showHourBankBalance && <th className="text-end">{t('organization.hourBankBalance')}</th>}
                </tr>
              </thead>
              <tbody>
                {engagements.map((eng) => (
                  <tr key={eng.id}>
                    <td>
                      <Link to={`/engagements/${eng.id}`} className="font-medium text-surface-900 hover:text-primary-700 hover:underline underline-offset-2">
                        {eng.child_name || eng.id}
                      </Link>
                    </td>
                    <td className="hidden sm:table-cell text-surface-600">{typeLabel}</td>
                    {showHourBankBalance && <td className="text-end"><HourBankBalanceCell engagementId={eng.id} /></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </HubTable>
        </HubSurface>
      )}
    </HubPanel>
  );
}

/** Remaining / purchased hours of one hour bank, from its canonical snapshot. */
function HourBankBalanceCell({ engagementId }: { engagementId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['hour-bank', engagementId],
    queryFn: () => api.getHourBank(engagementId),
  });
  const snap = data?.data;
  if (isLoading) return <span className="text-xs text-surface-500" aria-label={t('common.loading')}>…</span>;
  if (isError || !snap) return <span className="text-xs text-surface-500">—</span>;
  // EQ-014: the same figures as the bank page - the available balance (never negative) + the locked hours.
  const { balance, lockedHours } = bankCellFigures(snap as never);
  const low = snap.purchased_hours != null && balance <= 0;
  return (
    <span className={`text-sm tabular-nums whitespace-nowrap ${low ? 'text-danger-700 font-medium' : 'text-surface-900'}`}>
      {formatNumber(balance, 1)}
      {snap.purchased_hours != null && (
        <span className="text-xs text-surface-500"> / {formatNumber(snap.purchased_hours, 1)}</span>
      )}
      <span className="text-xs text-surface-500"> {t('common.hoursShort')}</span>
      {lockedHours > 0 && (
        <span className="text-xs text-warning-700 ms-1.5" data-testid="bank-cell-locked">
          {t('engagementPage.hbLockedShort', { hours: formatNumber(lockedHours, 2) })}
        </span>
      )}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Tasks / Service Requests
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * "משימות" and "קריאות שירות" are two views of the same canonical ticket
 * model, split by `task_type` — 'task' and 'ticket' respectively. Nothing is
 * rebuilt here: the rows come from the customer's ticket endpoint and link to
 * the existing ticket screen.
 *
 * The endpoint returns root tickets (no `parent_ticket_id`) and cannot filter
 * by `task_type`, so the split is done on the returned rows. That is a
 * presentation filter over canonical data, not a second source of truth.
 */
export function TicketsTab({ orgId, mode, addButton, canCreateTask = false }: {
  orgId: string;
  mode: 'ticket' | 'task';
  /** P-01/C4 — "+ פתיחת קריאה" on the service-requests tab. */
  addButton?: ReactNode;
  /** P-02b (§6.4) — "+ משימה חדשה" on the tasks tab (the customer is locked in the form). */
  canCreateTask?: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [creatingTask, setCreatingTask] = useState(false);

  // Service requests: the direct ones, as before. Tasks (P-02b, D-36 Q-P9):
  // EVERY task of the customer, from every engagement — no `direct_only`.
  const { data, isLoading, isError } = useQuery({
    queryKey: mode === 'task' ? ['org-tasks', orgId] : ['org-direct-tickets', orgId],
    queryFn: () => (mode === 'task'
      ? api.getOrganizationTickets(orgId, { task_type: 'task', limit: 200 })
      : api.getOrganizationTickets(orgId, { direct_only: 'true', limit: 50 })),
    enabled: !!orgId,
  });

  const all = data?.data ?? [];
  const rows = all.filter((tk: Ticket) => (tk as any).task_type === mode);

  return (
    <HubPanel
      title={mode === 'ticket' ? t('organization.tabServiceRequests') : t('organization.tabTasks')}
      subtitle={mode === 'ticket' ? t('organization.serviceRequestsSubtitle') : t('organization.tasksSubtitle')}
      action={
        <>
          <ForwardLink to={mode === 'task' ? '/tasks' : '/tickets'}>{t('common.viewAll')}</ForwardLink>
          {addButton}
          {mode === 'task' && canCreateTask && (
            <button type="button" className="btn btn-secondary" onClick={() => setCreatingTask(true)}>
              <Plus size={16} aria-hidden="true" /> {t('organization.newTask')}
            </button>
          )}
        </>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <InlineError>{t('tickets.loadError')}</InlineError>
      ) : rows.length === 0 ? (
        <HubSurface>
          <EmptyState
            icon={mode === 'ticket' ? LifeBuoy : ListChecks}
            title={mode === 'ticket' ? t('organization.noServiceRequests') : t('organization.noTasks')}
          />
        </HubSurface>
      ) : (
        <HubSurface>
          {mode === 'task' ? (
            // P-06/E2 (D-62) — the same task row as "כל המשימות" (▶, columns, ⋯ with 📎).
            <TaskRowsTable
              rows={rows as any[]}
              caption={t('organization.tabTasks')}
              hide={['customer']}
              withManageActions
              onChanged={() => {
                queryClient.invalidateQueries({ queryKey: ['org-tasks', orgId] });
                queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
              }}
            />
          ) : (
            <TicketRows tickets={rows} showEngagement={false} />
          )}
        </HubSurface>
      )}
      {creatingTask && (
        <TaskFormModal
          mode="create"
          placement={{ organizationId: orgId }}
          lock="customer"
          onClose={() => setCreatingTask(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['org-tasks', orgId] });
            queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
            setCreatingTask(false);
          }}
        />
      )}
    </HubPanel>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   SLA settings (admin only)
   ══════════════════════════════════════════════════════════════════════════ */

/** SLA hours are positive numbers; business hours are required and end after they start. */
const SLA_SCHEMA: FormSchema = {
  'org-sla-response': [rules.required(), rules.numberAbove(0)],
  'org-sla-resolution': [rules.required(), rules.numberAbove(0)],
  'org-sla-bh-start': [rules.required()],
  'org-sla-bh-end': [rules.required(), slaEndAfterStart],
};

/** "18:00" after "09:00" (same-day business hours). */
function slaEndAfterStart(value: unknown, values: Record<string, unknown>) {
  const end = String(value ?? '');
  const start = String(values['org-sla-bh-start'] ?? '');
  return end && start && end <= start ? { key: 'form.endAfterStart' } : null;
}

function SlaSettingsSection({ orgId, org }: { orgId: string; org: any }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [responseHours, setResponseHours] = useState(String(org.sla_response_hours ?? 1));
  const [resolutionHours, setResolutionHours] = useState(String(org.sla_resolution_hours ?? 48));
  const [bhStart, setBhStart] = useState(String(org.business_hours_start ?? '09:00').slice(0, 5)); // DQ-131: HH:mm, no seconds
  const [bhEnd, setBhEnd] = useState(String(org.business_hours_end ?? '18:00').slice(0, 5));
  const [days, setDays] = useState<number[]>(org.business_days ?? [0, 1, 2, 3, 4]);

  const dayLabels = [
    t('sla.sun'), t('sla.mon'), t('sla.tue'), t('sla.wed'),
    t('sla.thu'), t('sla.fri'), t('sla.sat'),
  ];

  const mutation = useMutation({
    mutationFn: () =>
      api.updateOrganizationSla(orgId, {
        sla_response_hours: parseFloat(responseHours.replace(',', '.')),
        sla_resolution_hours: parseFloat(resolutionHours.replace(',', '.')),
        business_hours_start: bhStart,
        business_hours_end: bhEnd,
        business_days: days,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
      setEditing(false);
    },
  });

  const sla = useFormValidation(SLA_SCHEMA, {
    'org-sla-response': responseHours,
    'org-sla-resolution': resolutionHours,
    'org-sla-bh-start': bhStart,
    'org-sla-bh-end': bhEnd,
  });

  const toggleDay = (d: number) => {
    setDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());
  };

  const activeDays: number[] = org.business_days ?? [0, 1, 2, 3, 4];

  return (
    <HubPanel
      title={t('sla.slaSettings')}
      action={!editing ? (
        <button type="button" onClick={() => setEditing(true)} className="btn btn-secondary">
          <Pencil size={16} aria-hidden="true" /> {t('common.edit')}
        </button>
      ) : undefined}
    >
      {!editing ? (
        <HubSurface padded>
          <FactGrid
            columns={3}
            items={[
              { label: t('sla.responseTime'), value: <span className="tabular-nums">{t('sla.hoursValue', { count: Number(org.sla_response_hours ?? 1) })}</span> },
              { label: t('sla.resolutionTime'), value: <span className="tabular-nums">{t('sla.hoursValue', { count: Number(org.sla_resolution_hours ?? 48) })}</span> },
              {
                label: t('sla.businessHours'),
                value: <span className="tabular-nums" dir="ltr">{(org.business_hours_start ?? '09:00').slice(0, 5)} – {(org.business_hours_end ?? '18:00').slice(0, 5)}</span>,
              },
              {
                label: t('sla.businessDays'),
                wide: true,
                value: (
                  <span className="flex flex-wrap gap-1.5 mt-1">
                    {dayLabels.map((label, i) => (
                      activeDays.includes(i) ? (
                        <Badge key={i} tone="brand" className="border border-transparent">{label}</Badge>
                      ) : (
                        // A day off: outline + muted text (a token colour that keeps 4.5:1, not opacity), and named for screen readers.
                        <span key={i} className="inline-flex items-center whitespace-nowrap rounded-full border border-dashed border-border px-2 py-0.5 text-xs font-medium text-text-3">
                          {label}<span className="sr-only"> ({t('sla.dayOff')})</span>
                        </span>
                      )
                    ))}
                  </span>
                ),
              },
            ]}
          />
        </HubSurface>
      ) : (
        <HubSurface padded>
          <form className="space-y-4" noValidate onSubmit={sla.submit(() => mutation.mutate())}>
            {sla.showSummary && (
              <FormErrorSummary
                errors={sla.errors}
                labels={{
                  'org-sla-response': t('sla.responseTime'),
                  'org-sla-resolution': t('sla.resolutionTime'),
                  'org-sla-bh-start': `${t('sla.businessHours')} · ${t('common.start')}`,
                  'org-sla-bh-end': `${t('sla.businessHours')} · ${t('common.end')}`,
                }}
                order={Object.keys(SLA_SCHEMA)}
              />
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field id="org-sla-response" label={t('sla.responseTime')} required hint={t('form.hoursHint')} error={sla.errors['org-sla-response']}>
                <input type="text" inputMode="decimal" dir="ltr" className="input text-start tabular-nums" value={responseHours}
                  onChange={(e) => setResponseHours(e.target.value)} onBlur={() => sla.blur('org-sla-response')} />
              </Field>
              <Field id="org-sla-resolution" label={t('sla.resolutionTime')} required hint={t('form.hoursHint')} error={sla.errors['org-sla-resolution']}>
                <input type="text" inputMode="decimal" dir="ltr" className="input text-start tabular-nums" value={resolutionHours}
                  onChange={(e) => setResolutionHours(e.target.value)} onBlur={() => sla.blur('org-sla-resolution')} />
              </Field>
              <Field id="org-sla-bh-start" label={`${t('sla.businessHours')} · ${t('common.start')}`} required error={sla.errors['org-sla-bh-start']}>
                <TimeTextInput dir="ltr" className="input text-start" value={bhStart}
                  onChange={(v) => setBhStart(v)} onBlur={() => sla.blur('org-sla-bh-start')} />
              </Field>
              <Field id="org-sla-bh-end" label={`${t('sla.businessHours')} · ${t('common.end')}`} required error={sla.errors['org-sla-bh-end']}>
                <TimeTextInput dir="ltr" className="input text-start" value={bhEnd}
                  onChange={(v) => setBhEnd(v)} onBlur={() => sla.blur('org-sla-bh-end')} />
              </Field>
            </div>
            <fieldset>
              <legend className="label">{t('sla.businessDays')}</legend>
              <div className="flex gap-2 flex-wrap">
                {dayLabels.map((label, i) => (
                  <button key={i} type="button"
                    onClick={() => toggleDay(i)}
                    aria-pressed={days.includes(i)}
                    className={`min-w-10 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors duration-150 ${
                      days.includes(i)
                        ? 'bg-primary-50 text-primary-800 border-primary-300'
                        : 'bg-surface-0 text-surface-600 border-surface-300 hover:bg-surface-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </fieldset>
            <FormServerError error={mutation.error} fallback={t('common.saveError')} />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setEditing(false); mutation.reset(); sla.reset(); }} className="btn btn-secondary">
                {t('common.cancel')}
              </button>
              <button type="submit" disabled={mutation.isPending} aria-busy={mutation.isPending || undefined} className="btn btn-primary">
                {mutation.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
                {t('form.save')}
              </button>
            </div>
          </form>
        </HubSurface>
      )}
    </HubPanel>
  );
}
